<template>
  <v-container fluid>
    <!-- User Management Not Available Message -->
    <v-row v-if="!userManagementAvailable">
      <v-col cols="12">
        <v-alert type="warning" outlined>
          <v-icon left>mdi-alert</v-icon>
          <strong>Tool Management Not Available</strong>
          <div class="mt-2">
            The advanced tool management features are not available in this deployment. 
            Please use the standard User Management page to manage permissions.
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Main Tool Admin Interface -->
    <v-row v-else>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-icon left color="primary" large>{{ toolIcon }}</v-icon>
          <div>
            <h1>{{ toolName }} Management</h1>
            <p class="text--secondary mb-0">Manage user access and permissions for {{ toolName }}</p>
          </div>
        </div>

        <!-- Tool Statistics -->
        <v-row class="mb-4">
          <v-col cols="3">
            <v-card color="blue lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ toolStats.totalUsers }}</div>
                <div>Total Users</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="green lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ toolStats.activeUsers }}</div>
                <div>With Access</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="orange lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ toolStats.managers }}</div>
                <div>Managers</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="grey lighten-3">
              <v-card-text class="text-center">
                <div class="text-h4">{{ toolStats.noAccess }}</div>
                <div>No Access</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Users Table -->
        <v-card>
          <v-card-title>
            User Access Control
            <v-spacer></v-spacer>
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Search users..."
              single-line
              hide-details
              class="mr-4"
              style="max-width: 300px;"
            ></v-text-field>
            <v-btn color="primary" @click="showGrantAccessDialog = true">
              <v-icon left>mdi-account-plus</v-icon>
              Grant Access
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <v-data-table
              :headers="userHeaders"
              :items="toolUsers"
              :search="userSearch"
              :loading="loadingUsers"
              class="elevation-1"
            >
              <template v-slot:item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" small>
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.access="{ item }">
                <v-switch
                  :value="hasToolAccess(item)"
                  @change="updateToolAccess(item, $event)"
                  dense
                  :disabled="item.status !== 'active'"
                ></v-switch>
              </template>

              <template v-slot:item.manager="{ item }">
                <v-switch
                  :value="isToolManager(item)"
                  @change="updateToolManager(item, $event)"
                  dense
                  :disabled="!hasToolAccess(item) || item.status !== 'active'"
                ></v-switch>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click="viewUserDetails(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn icon small @click="removeToolAccess(item)" :disabled="!hasToolAccess(item)">
                  <v-icon small>mdi-account-remove</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Grant Access Dialog -->
    <v-dialog v-model="showGrantAccessDialog" max-width="600px">
      <v-card>
        <v-card-title>Grant {{ toolName }} Access</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedUsers"
            :items="availableUsers"
            item-text="email"
            item-value="email"
            label="Select users to grant access"
            multiple
            chips
            deletable-chips
            :search-input.sync="userSearchInput"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.email }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.department }} - {{ item.roles.join(', ') }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          
          <v-checkbox
            v-model="grantManagerAccess"
            label="Also grant manager permissions"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showGrantAccessDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="grantAccess" :loading="grantingAccess">Grant Access</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- User Details Dialog -->
    <v-dialog v-model="showUserDialog" max-width="500px">
      <v-card v-if="selectedUser">
        <v-card-title>{{ selectedUser.email }}</v-card-title>
        <v-card-text>
          <p><strong>Department:</strong> {{ selectedUser.department }}</p>
          <p><strong>Roles:</strong> {{ selectedUser.roles.join(', ') }}</p>
          <p><strong>Status:</strong> {{ selectedUser.status }}</p>
          <p><strong>Last Login:</strong> {{ formatDate(selectedUser.last_login) }}</p>
          
          <div class="mt-4">
            <h3>{{ toolName }} Permissions</h3>
            <v-chip :color="hasToolAccess(selectedUser) ? 'green' : 'grey'" class="mr-2">
              {{ hasToolAccess(selectedUser) ? 'Has Access' : 'No Access' }}
            </v-chip>
            <v-chip v-if="isToolManager(selectedUser)" color="orange">
              Manager
            </v-chip>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showUserDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbars -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { getFirestore } from 'firebase/firestore'
import { 
  getAllUsers, 
  updateToolPermissions,
  isUserManagementAvailable
} from '@/utils/userManagementOptional'

export default {
  name: 'ToolAdmin',
  props: {
    user: {
      type: Object,
      required: true
    },
    toolId: {
      type: String,
      required: true
    },
    toolName: {
      type: String,
      required: true
    },
    toolIcon: {
      type: String,
      default: 'mdi-tool'
    }
  },
  
  data() {
    return {
      // Users data
      allUsers: [],
      toolUsers: [],
      loadingUsers: false,
      userSearch: '',
      
      // Statistics
      toolStats: {
        totalUsers: 0,
        activeUsers: 0,
        managers: 0,
        noAccess: 0
      },
      
      // Dialogs
      showGrantAccessDialog: false,
      showUserDialog: false,
      selectedUser: null,
      
      // Grant access
      selectedUsers: [],
      availableUsers: [],
      userSearchInput: '',
      grantManagerAccess: false,
      grantingAccess: false,
      
      // UI
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      
      // Table headers
      userHeaders: [
        { text: 'Email', value: 'email' },
        { text: 'Department', value: 'department' },
        { text: 'Status', value: 'status' },
        { text: 'Access', value: 'access', sortable: false },
        { text: 'Manager', value: 'manager', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      userManagementAvailable: false
    }
  },
  
  created() {
    this.loadUsers()
    this.userManagementAvailable = isUserManagementAvailable()
  },
  
  watch: {
    showGrantAccessDialog(val) {
      if (val) {
        this.loadAvailableUsers()
      }
    }
  },
  
  methods: {
    async loadUsers() {
      this.loadingUsers = true
      try {
        this.allUsers = await getAllUsers()
        this.toolUsers = this.allUsers.filter(user => user.status === 'active')
        this.calculateStats()
      } catch (error) {
        this.showError('Error loading users: ' + error.message)
      }
      this.loadingUsers = false
    },
    
    loadAvailableUsers() {
      // Show users who don't have access to this tool
      this.availableUsers = this.allUsers.filter(user => 
        user.status === 'active' && !this.hasToolAccess(user)
      )
    },
    
    calculateStats() {
      this.toolStats = {
        totalUsers: this.allUsers.filter(u => u.status === 'active').length,
        activeUsers: this.allUsers.filter(u => this.hasToolAccess(u)).length,
        managers: this.allUsers.filter(u => this.isToolManager(u)).length,
        noAccess: this.allUsers.filter(u => u.status === 'active' && !this.hasToolAccess(u)).length
      }
    },
    
    hasToolAccess(user) {
      return user.tool_permissions?.[this.toolId]?.access === true
    },
    
    isToolManager(user) {
      return user.tool_permissions?.[this.toolId]?.manager === true
    },
    
    async updateToolAccess(user, hasAccess) {
      try {
        const currentManager = this.isToolManager(user)
        await updateToolPermissions(user.email, this.toolId, hasAccess, currentManager)
        
        // Update local data
        if (!user.tool_permissions) user.tool_permissions = {}
        if (!user.tool_permissions[this.toolId]) user.tool_permissions[this.toolId] = {}
        user.tool_permissions[this.toolId].access = hasAccess
        
        // If removing access, also remove manager
        if (!hasAccess && currentManager) {
          user.tool_permissions[this.toolId].manager = false
        }
        
        this.calculateStats()
        this.showSuccess(`Updated access for ${user.email}`)
      } catch (error) {
        this.showError('Error updating access: ' + error.message)
      }
    },
    
    async updateToolManager(user, isManager) {
      try {
        const currentAccess = this.hasToolAccess(user)
        await updateToolPermissions(user.email, this.toolId, currentAccess, isManager)
        
        // Update local data
        if (!user.tool_permissions) user.tool_permissions = {}
        if (!user.tool_permissions[this.toolId]) user.tool_permissions[this.toolId] = {}
        user.tool_permissions[this.toolId].manager = isManager
        
        this.calculateStats()
        this.showSuccess(`Updated manager status for ${user.email}`)
      } catch (error) {
        this.showError('Error updating manager status: ' + error.message)
      }
    },
    
    async grantAccess() {
      this.grantingAccess = true
      try {
        for (const userEmail of this.selectedUsers) {
          await updateToolPermissions(
            userEmail, 
            this.toolId, 
            true, 
            this.grantManagerAccess
          )
        }
        
        this.showSuccess(`Granted access to ${this.selectedUsers.length} user(s)`)
        this.showGrantAccessDialog = false
        this.selectedUsers = []
        this.grantManagerAccess = false
        this.loadUsers()
      } catch (error) {
        this.showError('Error granting access: ' + error.message)
      }
      this.grantingAccess = false
    },
    
    async removeToolAccess(user) {
      try {
        await updateToolPermissions(user.email, this.toolId, false, false)
        
        // Update local data
        if (user.tool_permissions && user.tool_permissions[this.toolId]) {
          user.tool_permissions[this.toolId] = { access: false, manager: false }
        }
        
        this.calculateStats()
        this.showSuccess(`Removed access for ${user.email}`)
      } catch (error) {
        this.showError('Error removing access: ' + error.message)
      }
    },
    
    viewUserDetails(user) {
      this.selectedUser = user
      this.showUserDialog = true
    },
    
    getStatusColor(status) {
      switch (status) {
        case 'active': return 'green'
        case 'pending': return 'orange'
        case 'inactive': return 'red'
        default: return 'grey'
      }
    },
    
    formatDate(date) {
      if (!date) return 'Never'
      return new Date(date.seconds ? date.seconds * 1000 : date).toLocaleDateString()
    },
    
    showSuccess(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'success'
      this.showSnackbar = true
    },
    
    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.showSnackbar = true
    }
  }
}
</script> 